import { collectHitsInTabs, type FindHit } from "../search/findInOpenedTabs.js";
import { collectHitsInDirectory } from "../search/findInDirectory.js";
import { countOccurrences, findNextIndex, findPrevIndex, replaceFirstOccurrence } from "../text/findInText.js";
import { gotoLineOffset } from "../text/gotoLine.js";
import { getFindSession, setFindSession } from "../text/findSession.js";
import type { WebPrefs } from "../settings/webPrefs.js";
import { pickWorkspaceDirectory } from "../io/openWorkspaceFolder.js";

/** Find / Find Next (§16.3). */
export function openFindDialog(opts: {
  getText: () => string;
  getSelectionStart: () => number;
  setSelection: (start: number, end: number) => void;
}): void {
  closeSingletonDialog("npp-find");
  const prev = getFindSession();
  const dlg = document.createElement("dialog");
  dlg.className = "npp-dialog npp-dialog-search npp-dialog-find-files";
  dlg.dataset.singletonId = "npp-find";

  const isMobile = window.matchMedia("(pointer: coarse), (max-width: 40rem)").matches;

  if (isMobile) {
    // Compact bottom-bar toolbar
    dlg.innerHTML = `
      <div class="npp-find-bar">
        <div class="npp-find-bar-row">
          <input type="text" name="q" class="npp-find-bar-input"
                 autocomplete="off" spellcheck="false"
                 placeholder="Find…" aria-label="Find what" />
          <label class="npp-find-opt-toggle" title="Match case">
            <input type="checkbox" name="case" />
            <span class="npp-find-opt-chip">Aa</span>
          </label>
          <label class="npp-find-opt-toggle" title="Match whole word">
            <input type="checkbox" name="ww" />
            <span class="npp-find-opt-chip">W|</span>
          </label>
          <button type="button" value="prev" class="npp-find-icon-btn"
                  title="Find Previous" aria-label="Find Previous">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7.41 15.41 12 10.83l4.59 4.58L18 14l-6-6-6 6 1.41 1.41z"/></svg>
          </button>
          <button type="button" value="next" class="npp-find-icon-btn"
                  title="Find Next" aria-label="Find Next">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M16.59 8.59 12 13.17 7.41 8.59 6 10l6 6 6-6-1.41-1.41z"/></svg>
          </button>
          <button type="button" value="close" class="npp-find-icon-btn npp-find-icon-btn--close"
                  title="Close" aria-label="Close find bar">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
          </button>
        </div>
      </div>`;
  } else {
    // Original desktop floating dialog — unchanged
    dlg.innerHTML = `
      <div class="npp-dialog-form npp-dialog-search-form">
        <h2 class="npp-dialog-title">Find</h2>
        <label class="npp-dialog-row">Find what: <input type="text" name="q" class="npp-dialog-input" autocomplete="off" /></label>
        <label class="npp-dialog-row"><input type="checkbox" name="case" /> Match case</label>
        <label class="npp-dialog-row"><input type="checkbox" name="ww" /> Match whole word only</label>
        <div class="npp-dialog-actions">
          <button type="button" value="next">Find Next</button>
          <button type="button" value="prev">Find Previous</button>
          <button type="button" value="close">Close</button>
        </div>
      </div>`;
  }

  document.body.append(dlg);

  // Both layouts use the same name= attributes, so these selectors work for both.
  const q = dlg.querySelector<HTMLInputElement>('input[name="q"]')!;
  const caseBox = dlg.querySelector<HTMLInputElement>('input[name="case"]')!;
  const wwBox = dlg.querySelector<HTMLInputElement>('input[name="ww"]')!;
  const nextBtn = dlg.querySelector<HTMLButtonElement>('button[value="next"]')!;
  const prevBtn = dlg.querySelector<HTMLButtonElement>('button[value="prev"]')!;
  dlg.querySelector<HTMLButtonElement>('button[value="close"]')!.addEventListener("click", () => dlg.close());

  q.value = prev.needle;
  caseBox.checked = prev.caseSensitive;
  wwBox.checked = prev.wholeWord;

  let lastFrom = opts.getSelectionStart();

  const persist = (): void => {
    setFindSession({
      needle: q.value,
      caseSensitive: caseBox.checked,
      wholeWord: wwBox.checked,
    });
  };

  const findNext = (): void => {
    persist();
    const text = opts.getText();
    const needle = q.value;
    if (!needle) return;
    const cs = caseBox.checked;
    const ww = wwBox.checked;
    let i = findNextIndex(text, needle, lastFrom, cs, ww);
    if (i < 0 && lastFrom > 0) {
      const ok = confirm("End of file reached. Continue from the start?");
      if (!ok) return;
      lastFrom = 0;
      i = findNextIndex(text, needle, 0, cs, ww);
    }
    if (i < 0) return;
    opts.setSelection(i, i + needle.length);
    lastFrom = i + needle.length;
  };

  const findPrev = (): void => {
    persist();
    const text = opts.getText();
    const needle = q.value;
    if (!needle) return;
    const cs = caseBox.checked;
    const ww = wwBox.checked;
    const caret = opts.getSelectionStart();
    let i = findPrevIndex(text, needle, caret, cs, ww);
    if (i < 0 && caret < text.length) {
      const ok = confirm("Start of file reached. Continue from the end?");
      if (!ok) return;
      i = findPrevIndex(text, needle, text.length, cs, ww);
    }
    if (i < 0) return;
    opts.setSelection(i, i + needle.length);
    lastFrom = i;
  };

  nextBtn.addEventListener("click", findNext);
  prevBtn.addEventListener("click", findPrev);
  dlg.addEventListener("close", () => {
    if (q.value.trim()) persist();
    dlg.remove();
  });
  dlg.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && (e.target as HTMLElement) === q) {
      e.preventDefault();
      if (e.shiftKey) findPrev(); else findNext();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      dlg.close();
    }
  });

  dlg.show();
  if (!isMobile) {
    makeDraggable(dlg, dlg.querySelector<HTMLElement>(".npp-dialog-title")!);
  }
  q.focus();
}

/** Replace subset (§16.3). */
export function openReplaceDialog(opts: {
  getText: () => string;
  setText: (t: string) => void;
  getSelectionStart: () => number;
  setSelection: (start: number, end: number) => void;
}): void {
  const dlg = document.createElement("dialog");
  dlg.className = "npp-dialog npp-dialog-search";
  dlg.innerHTML = `
    <div class="npp-dialog-form npp-dialog-search-form">
      <h2 class="npp-dialog-title">Replace</h2>
      <label class="npp-dialog-row">Find: <input type="text" name="find" class="npp-dialog-input" /></label>
      <label class="npp-dialog-row">Replace: <input type="text" name="rep" class="npp-dialog-input" /></label>
      <label class="npp-dialog-row"><input type="checkbox" name="case" /> Match case</label>
      <label class="npp-dialog-row"><input type="checkbox" name="ww" /> Match whole word only</label>
      <div class="npp-dialog-actions">
        <button type="button" value="one">Replace</button>
        <button type="button" value="all">Replace All</button>
        <button type="button" value="x">Close</button>
      </div>
    </div>`;
  document.body.append(dlg);
  const find = dlg.querySelector<HTMLInputElement>('input[name="find"]')!;
  const rep = dlg.querySelector<HTMLInputElement>('input[name="rep"]')!;
  const caseBox = dlg.querySelector<HTMLInputElement>('input[name="case"]')!;
  const wwBox = dlg.querySelector<HTMLInputElement>('input[name="ww"]')!;
  dlg.querySelector('button[value="one"]')!.addEventListener("click", () => {
    const r = replaceFirstOccurrence(
      opts.getText(),
      find.value,
      rep.value,
      caseBox.checked,
      wwBox.checked,
    );
    if (r.replaced) {
      opts.setText(r.text);
      opts.setSelection(r.selectionStart, r.selectionEnd);
    }
  });
  dlg.querySelector<HTMLButtonElement>('button[value="x"]')!.addEventListener("click", () => dlg.close());
  dlg.querySelector('button[value="all"]')!.addEventListener("click", () => {
    let t = opts.getText();
    const f = find.value;
    const rv = rep.value;
    const cs = caseBox.checked;
    const ww = wwBox.checked;
    if (!f) return;
    let guard = 0;
    while (guard++ < 100000) {
      const r = replaceFirstOccurrence(t, f, rv, cs, ww);
      if (!r.replaced) break;
      t = r.text;
    }
    opts.setText(t);
  });
  dlg.addEventListener("close", () => dlg.remove());
  dlg.showModal();
  find.focus();
}

export function openGotoLineDialog(opts: {
  getText: () => string;
  setSelection: (start: number, end: number) => void;
}): void {
  const dlg = document.createElement("dialog");
  dlg.className = "npp-dialog npp-dialog-search";
  dlg.innerHTML = `
    <form method="dialog" class="npp-dialog-form npp-dialog-search-form">
      <h2 class="npp-dialog-title">Go to Line</h2>
      <label class="npp-dialog-row">Line (1-based): <input type="number" name="line" min="1" class="npp-dialog-input" /></label>
      <div class="npp-dialog-actions">
        <button type="submit" value="go">Go</button>
        <button type="submit" value="x">Cancel</button>
      </div>
    </form>`;
  document.body.append(dlg);
  const form = dlg.querySelector("form")!;
  const line = dlg.querySelector<HTMLInputElement>('input[name="line"]')!;
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const sub = (e as SubmitEvent).submitter as HTMLButtonElement | null;
    if (!sub || sub.value !== "go") {
      dlg.close();
      return;
    }
    const n = parseInt(line.value, 10);
    if (!Number.isFinite(n) || n < 1) return;
    const off = gotoLineOffset(opts.getText(), n);
    opts.setSelection(off, off);
    dlg.close();
  });
  dlg.addEventListener("close", () => dlg.remove());
  dlg.showModal();
  line.focus();
}

export function openPreferencesDialog(opts: {
  initial: WebPrefs;
  onSave: (p: Pick<WebPrefs, "defaultWordWrap" | "editorBaseFontPx" | "nativeSpellcheckEnabled">) => void;
  /** Opens the Shortcut Mapper (Settings → keyboard). */
  onOpenShortcutMapper?: () => void;
}): void {
  const dlg = document.createElement("dialog");
  dlg.className = "npp-dialog";
  dlg.innerHTML = `
    <form method="dialog" class="npp-dialog-form">
      <h2 class="npp-dialog-title">Preferences</h2>
      <label class="npp-dialog-row"><input type="checkbox" name="wrap" /> Default word wrap for new sessions</label>
      <label class="npp-dialog-row"><input type="checkbox" name="spell" /> Spell checking (browser + prose highlights where supported)</label>
      <label class="npp-dialog-row">Editor base font (px): <input type="number" name="font" min="10" max="32" class="npp-dialog-input" style="max-width:5rem" /></label>
      <div class="npp-dialog-row npp-prefs-shortcut-row">
        <button type="button" name="shortcuts" class="npp-prefs-shortcuts-btn">Shortcut Mapper…</button>
      </div>
      <p class="npp-dialog-hint">Word wrap is also saved when you toggle View → Word wrap. Max buffers never soft-wrap. Font updates immediately. Shortcuts are stored in this browser.</p>
      <div class="npp-dialog-actions">
        <button type="submit" value="ok">OK</button>
        <button type="submit" value="cancel">Cancel</button>
      </div>
    </form>`;
  document.body.append(dlg);
  const wrap = dlg.querySelector<HTMLInputElement>('input[name="wrap"]')!;
  const spell = dlg.querySelector<HTMLInputElement>('input[name="spell"]')!;
  const font = dlg.querySelector<HTMLInputElement>('input[name="font"]')!;
  const shortcutsBtn = dlg.querySelector<HTMLButtonElement>(".npp-prefs-shortcuts-btn")!;
  wrap.checked = opts.initial.defaultWordWrap;
  spell.checked = opts.initial.nativeSpellcheckEnabled === true;
  font.value = String(opts.initial.editorBaseFontPx);
  if (opts.onOpenShortcutMapper) {
    shortcutsBtn.addEventListener("click", () => opts.onOpenShortcutMapper!());
  } else {
    shortcutsBtn.hidden = true;
  }
  const form = dlg.querySelector("form")!;
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const sub = (e as SubmitEvent).submitter as HTMLButtonElement | null;
    if (!sub || sub.value !== "ok") {
      dlg.close();
      return;
    }
    const px = parseInt(font.value, 10);
    opts.onSave({
      defaultWordWrap: wrap.checked,
      nativeSpellcheckEnabled: spell.checked,
      editorBaseFontPx: Number.isFinite(px) ? Math.min(32, Math.max(10, px)) : opts.initial.editorBaseFontPx,
    });
    dlg.close();
  });
  dlg.addEventListener("close", () => dlg.remove());
  dlg.showModal();
}

export function openWindowsDialog(opts: {
  titles: readonly string[];
  activeIndex: number;
  onSelect: (index: number) => void;
}): void {
  const dlg = document.createElement("dialog");
  dlg.className = "npp-dialog npp-dialog-windows";
  const list = opts.titles
    .map(
      (t, i) =>
        `<li><button type="button" class="npp-win-item" data-i="${i}" ${i === opts.activeIndex ? 'data-active="true"' : ""}>${escapeHtml(t)}</button></li>`,
    )
    .join("");
  dlg.innerHTML = `
    <div class="npp-dialog-form">
      <h2 class="npp-dialog-title">Windows</h2>
      <ul class="npp-win-list">${list}</ul>
      <div class="npp-dialog-actions">
        <button type="button" value="x">Close</button>
      </div>
    </div>`;
  document.body.append(dlg);
  dlg.querySelector<HTMLButtonElement>('button[value="x"]')!.addEventListener("click", () => dlg.close());
  dlg.querySelectorAll<HTMLButtonElement>(".npp-win-item").forEach((btn) => {
    btn.addEventListener("click", () => {
      const i = parseInt(btn.dataset.i ?? "-1", 10);
      if (i >= 0) opts.onSelect(i);
      dlg.close();
    });
  });
  dlg.addEventListener("close", () => dlg.remove());
  dlg.showModal();
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Re-export for callers that only import from dialogs. */
export type { FindHit } from "../search/findInOpenedTabs.js";

/** Count matches in current buffer (Find session defaults + override field). */
export function openCountDialog(opts: {
  getText: () => string;
  initialNeedle: string;
  initialCase: boolean;
  initialWholeWord: boolean;
}): void {
  const dlg = document.createElement("dialog");
  dlg.className = "npp-dialog npp-dialog-search";
  dlg.innerHTML = `
    <div class="npp-dialog-form npp-dialog-search-form">
      <h2 class="npp-dialog-title">Count</h2>
      <label class="npp-dialog-row">Find what: <input type="text" name="q" class="npp-dialog-input" autocomplete="off" /></label>
      <label class="npp-dialog-row"><input type="checkbox" name="case" /> Match case</label>
      <label class="npp-dialog-row"><input type="checkbox" name="ww" /> Match whole word only</label>
      <p class="npp-dialog-count-out" data-role="out"></p>
      <div class="npp-dialog-actions">
        <button type="button" value="go">Count</button>
        <button type="button" value="x">Close</button>
      </div>
    </div>`;
  document.body.append(dlg);
  const q = dlg.querySelector<HTMLInputElement>('input[name="q"]')!;
  const caseBox = dlg.querySelector<HTMLInputElement>('input[name="case"]')!;
  const wwBox = dlg.querySelector<HTMLInputElement>('input[name="ww"]')!;
  const out = dlg.querySelector<HTMLElement>('[data-role="out"]')!;
  q.value = opts.initialNeedle;
  caseBox.checked = opts.initialCase;
  wwBox.checked = opts.initialWholeWord;
  const run = (): void => {
    const n = countOccurrences(opts.getText(), q.value, caseBox.checked, wwBox.checked);
    out.textContent = q.value ? `Matches: ${n}` : "Enter text to find.";
  };
  dlg.querySelector<HTMLButtonElement>('button[value="go"]')!.addEventListener("click", run);
  dlg.querySelector<HTMLButtonElement>('button[value="x"]')!.addEventListener("click", () => dlg.close());
  dlg.addEventListener("close", () => dlg.remove());
  dlg.showModal();
  q.focus();
}

/** Incremental search: refine as you type; jumps to first match from caret (wrap). */
export function openIncrementalSearchDialog(opts: {
  getText: () => string;
  getCaret: () => number;
  setSelection: (start: number, end: number) => void;
}): void {
  const dlg = document.createElement("dialog");
  dlg.className = "npp-dialog npp-dialog-search";
  dlg.innerHTML = `
    <div class="npp-dialog-form npp-dialog-search-form">
      <h2 class="npp-dialog-title">Incremental Search</h2>
      <label class="npp-dialog-row">Find: <input type="text" name="q" class="npp-dialog-input" autocomplete="off" /></label>
      <label class="npp-dialog-row"><input type="checkbox" name="case" /> Match case</label>
      <p class="npp-dialog-hint">Typing updates the first match from the caret (wraps).</p>
      <div class="npp-dialog-actions">
        <button type="button" value="x">Close</button>
      </div>
    </div>`;
  document.body.append(dlg);
  const q = dlg.querySelector<HTMLInputElement>('input[name="q"]')!;
  const caseBox = dlg.querySelector<HTMLInputElement>('input[name="case"]')!;
  const jump = (): void => {
    const needle = q.value;
    const text = opts.getText();
    if (!needle) return;
    const cs = caseBox.checked;
    const from = opts.getCaret();
    let i = findNextIndex(text, needle, from, cs, false);
    if (i < 0) i = findNextIndex(text, needle, 0, cs, false);
    if (i >= 0) opts.setSelection(i, i + needle.length);
  };
  q.addEventListener("input", jump);
  caseBox.addEventListener("change", jump);
  dlg.querySelector<HTMLButtonElement>('button[value="x"]')!.addEventListener("click", () => dlg.close());
  dlg.addEventListener("close", () => dlg.remove());
  dlg.showModal();
  q.focus();
}

type FindScope = "tabs" | "directory";

/**
 * Find in Files / Find in Directory.
 */
export function openFindInFilesDialog(opts: {
  tabs: readonly { title: string; content: string }[];
  initialScope?: FindScope;
  workspaceRoot: FileSystemDirectoryHandle | null;
  initialNeedle?: string;
  initialCase?: boolean;
  initialWholeWord?: boolean;
  initialFilters?: string;
  initialIncludeSubfolders?: boolean;
  onFindAll: (
    hits: FindHit[],
    meta: {
      needle: string;
      matchCase: boolean;
      wholeWord: boolean;
      scope: FindScope;
      directory?: string;
      filters?: string;
    },
  ) => void | Promise<void>;
}): void {
  closeSingletonDialog("npp-find-files");
  let scope: FindScope = opts.initialScope ?? "tabs";
  let selectedDir: FileSystemDirectoryHandle | null = opts.workspaceRoot ?? null;
  const dlg = document.createElement("dialog");
  dlg.className = "npp-dialog npp-dialog-search npp-dialog-find-files";
  dlg.dataset.singletonId = "npp-find-files";
  dlg.innerHTML = `
    <div class="npp-dialog-form npp-find-files-form">
      <h2 class="npp-dialog-title">Find in Files</h2>
      <p class="npp-find-files-subtitle">Search opened documents or an entire directory with filters.</p>
      <label class="npp-dialog-row npp-find-files-row">
        <span class="npp-find-files-label">Find what</span>
        <input type="text" name="q" class="npp-dialog-input" autocomplete="off" />
      </label>
      <label class="npp-dialog-row npp-find-files-row">
        <span class="npp-find-files-label">Filters</span>
        <input type="text" name="filters" class="npp-dialog-input" placeholder="*.ts;*.tsx;*.md" />
      </label>
      <div class="npp-find-files-scope" role="radiogroup" aria-label="Search scope">
        <label class="npp-find-files-scope-option"><input type="radio" name="scope" value="tabs" checked /> Opened documents</label>
        <label class="npp-find-files-scope-option"><input type="radio" name="scope" value="directory" /> Directory</label>
      </div>
      <div class="npp-dialog-row npp-find-files-row npp-find-files-dir-row">
        <span class="npp-find-files-label">Directory</span>
        <input type="text" name="dir" class="npp-dialog-input" readonly />
        <button type="button" value="browse" class="npp-find-files-browse">Browse…</button>
      </div>
      <div class="npp-find-files-options">
        <label class="npp-dialog-row npp-find-files-check"><input type="checkbox" name="case" /> Match case</label>
        <label class="npp-dialog-row npp-find-files-check"><input type="checkbox" name="ww" /> Match whole word only</label>
        <label class="npp-dialog-row npp-find-files-check"><input type="checkbox" name="sub" checked /> In all sub-folders</label>
      </div>
      <div class="npp-dialog-actions npp-find-files-actions">
        <button type="button" value="find" class="npp-find-files-find-btn">Find all</button>
        <button type="button" value="x">Cancel</button>
      </div>
    </div>`;
  document.body.append(dlg);
  const q = dlg.querySelector<HTMLInputElement>('input[name="q"]')!;
  const caseBox = dlg.querySelector<HTMLInputElement>('input[name="case"]')!;
  const wwBox = dlg.querySelector<HTMLInputElement>('input[name="ww"]')!;
  const filters = dlg.querySelector<HTMLInputElement>('input[name="filters"]')!;
  const dirInput = dlg.querySelector<HTMLInputElement>('input[name="dir"]')!;
  const subBox = dlg.querySelector<HTMLInputElement>('input[name="sub"]')!;
  const scopeTabs = dlg.querySelector<HTMLInputElement>('input[name="scope"][value="tabs"]')!;
  const scopeDir = dlg.querySelector<HTMLInputElement>('input[name="scope"][value="directory"]')!;
  const scopeTabsLabel = scopeTabs.closest("label");
  const scopeDirLabel = scopeDir.closest("label");
  const browseBtn = dlg.querySelector<HTMLButtonElement>('button[value="browse"]')!;
  const findBtn = dlg.querySelector<HTMLButtonElement>('button[value="find"]')!;
  q.value = opts.initialNeedle ?? "";
  if (opts.initialCase !== undefined) caseBox.checked = opts.initialCase;
  if (opts.initialWholeWord !== undefined) wwBox.checked = opts.initialWholeWord;
  filters.value = opts.initialFilters ?? "*.*";
  subBox.checked = opts.initialIncludeSubfolders ?? true;
  if (scope === "directory") scopeDir.checked = true;
  else scopeTabs.checked = true;
  dirInput.value = selectedDir?.name ?? "(none)";

  const syncScopeUi = (): void => {
    scope = scopeDir.checked ? "directory" : "tabs";
    const dirMode = scope === "directory";
    dirInput.disabled = !dirMode;
    browseBtn.disabled = !dirMode;
    subBox.disabled = !dirMode;
    filters.disabled = !dirMode;
    scopeTabsLabel?.classList.toggle("is-selected", scope === "tabs");
    scopeDirLabel?.classList.toggle("is-selected", scope === "directory");
  };
  const syncFindButtonState = (): void => {
    findBtn.disabled = q.value.trim().length === 0;
  };
  syncScopeUi();
  syncFindButtonState();
  scopeTabs.addEventListener("change", syncScopeUi);
  scopeDir.addEventListener("change", syncScopeUi);
  q.addEventListener("input", syncFindButtonState);
  browseBtn.addEventListener("click", async () => {
    const dir = await pickWorkspaceDirectory();
    if (!dir) return;
    selectedDir = dir;
    dirInput.value = dir.name;
  });

  findBtn.addEventListener("click", async () => {
    const needle = q.value.trim();
    if (!needle) return;
    findBtn.disabled = true;
    try {
      if (scope === "tabs") {
        const hits = collectHitsInTabs(opts.tabs, needle, caseBox.checked, wwBox.checked);
        await opts.onFindAll(hits, {
          needle,
          matchCase: caseBox.checked,
          wholeWord: wwBox.checked,
          scope: "tabs",
        });
      } else {
        const dir = selectedDir ?? opts.workspaceRoot;
        if (!dir) {
          void alert("Choose a directory first.");
          return;
        }
        const hits = await collectHitsInDirectory(
          dir,
          needle,
          caseBox.checked,
          wwBox.checked,
          subBox.checked,
          filters.value.trim() || "*.*",
        );
        await opts.onFindAll(hits, {
          needle,
          matchCase: caseBox.checked,
          wholeWord: wwBox.checked,
          scope: "directory",
          directory: dir.name,
          filters: filters.value.trim() || "*.*",
        });
      }
    } finally {
      findBtn.disabled = false;
    }
    dlg.close();
  });
  dlg.querySelector<HTMLButtonElement>('button[value="x"]')!.addEventListener("click", () => dlg.close());
  dlg.addEventListener("close", () => dlg.remove());
  dlg.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      dlg.close();
    }
  });

  // Non-modal so the editor stays interactive while the dialog is open.
  dlg.show();
  makeDraggable(dlg, dlg.querySelector<HTMLElement>(".npp-dialog-title")!);
  q.focus();
}

export function openFindInDirectoryDialog(opts: {
  tabs: readonly { title: string; content: string }[];
  workspaceRoot: FileSystemDirectoryHandle | null;
  initialNeedle?: string;
  initialCase?: boolean;
  initialWholeWord?: boolean;
  onFindAll: (
    hits: FindHit[],
    meta: {
      needle: string;
      matchCase: boolean;
      wholeWord: boolean;
      scope: FindScope;
      directory?: string;
      filters?: string;
    },
  ) => void | Promise<void>;
}): void {
  openFindInFilesDialog({
    ...opts,
    initialScope: "directory",
  });
}

/** Recent paths from localStorage (browser cannot reopen without a new picker). */
export function openRecentFilesDialog(paths: readonly string[]): void {
  if (!paths.length) {
    void alert("No recent files yet. Save or open a file first.");
    return;
  }
  const dlg = document.createElement("dialog");
  dlg.className = "npp-dialog npp-dialog-windows";
  const form = document.createElement("div");
  form.className = "npp-dialog-form";
  const h = document.createElement("h2");
  h.className = "npp-dialog-title";
  h.textContent = "Recent files";
  const hint = document.createElement("p");
  hint.className = "npp-dialog-hint";
  hint.innerHTML = "Paths are hints only — use <strong>File → Open</strong> to load again.";
  const ul = document.createElement("ul");
  ul.className = "npp-win-list npp-recent-list";
  for (const p of paths) {
    const li = document.createElement("li");
    li.className = "npp-recent-row";
    const span = document.createElement("span");
    span.className = "npp-recent-path";
    span.textContent = p;
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "npp-recent-copy";
    btn.textContent = "Copy path";
    btn.addEventListener("click", () => void navigator.clipboard.writeText(p));
    li.append(span, " ", btn);
    ul.appendChild(li);
  }
  const actions = document.createElement("div");
  actions.className = "npp-dialog-actions";
  const close = document.createElement("button");
  close.type = "button";
  close.textContent = "Close";
  close.addEventListener("click", () => dlg.close());
  actions.appendChild(close);
  form.append(h, hint, ul, actions);
  dlg.append(form);
  document.body.append(dlg);
  dlg.addEventListener("close", () => dlg.remove());
  dlg.showModal();
}

function closeSingletonDialog(id: string): void {
  const existing = document.querySelector<HTMLDialogElement>(`dialog[data-singleton-id="${id}"]`);
  if (existing) {
    existing.close();
    existing.remove();
  }
}

/** Make an element draggable via a handle (e.g. title bar). */
function makeDraggable(target: HTMLElement, handle: HTMLElement): void {
  handle.style.cursor = "move";
  handle.style.userSelect = "none";

  let startX = 0;
  let startY = 0;
  let origLeft = 0;
  let origTop = 0;

  const onMove = (e: PointerEvent): void => {
    target.style.left = `${origLeft + e.clientX - startX}px`;
    target.style.top = `${origTop + e.clientY - startY}px`;
  };

  const onUp = (): void => {
    document.removeEventListener("pointermove", onMove);
    document.removeEventListener("pointerup", onUp);
  };

  handle.addEventListener("pointerdown", (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const rect = target.getBoundingClientRect();
    startX = e.clientX;
    startY = e.clientY;
    origLeft = rect.left;
    origTop = rect.top;
    target.style.margin = "0";
    target.style.position = "fixed";
    target.style.left = `${rect.left}px`;
    target.style.top = `${rect.top}px`;
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  });
}
